/**
 * iGaming multi-level sub-affiliate (#156) — data-layer client.
 *
 * Thin typed wrapper over `apiClient` for the multi-level API surface:
 *   - /api/v1/igaming-multi-level/programs                   (create/upsert, get by offer)
 *   - /api/v1/igaming-multi-level/programs/:id/license       (ops license gate)
 *   - /api/v1/igaming-multi-level/programs/:id               (soft-delete)
 *   - /api/v1/igaming-multi-level/uplines                    (register parent edge)
 *   - /api/v1/igaming-multi-level/uplines/:affiliate_id      (fetch chain)
 *   - /api/v1/igaming-multi-level/calculate                  (dry-run cascade)
 *   - /api/v1/igaming-multi-level/commit                     (persist event)
 *   - /api/v1/igaming-multi-level/events                     (list events)
 *
 * Dependency-free beyond `apiClient`; all formatters are pure so the
 * unit-tests can exercise them directly without mounting the page.
 */
import { apiClient, ApiError } from "../../api-client";

// ---------------------------------------------------------------------------
// Types — mirror the backend view shape exactly (see routes/igaming-multi-level.ts).
// ---------------------------------------------------------------------------

export interface Program {
	id: string;
	offer_id: string;
	max_depth: number;
	level_bps: number[];
	license_ok: boolean;
	active: boolean;
	created_at: number;
	updated_at: number;
}

export interface Upline {
	id: string;
	affiliate_id: string;
	parent_id: string | null;
	depth_from_root: number;
	created_at: number;
	updated_at: number;
}

export interface LevelPayout {
	level: number;
	affiliate_id: string;
	share_bps: number;
	payout_cents: number;
}

export interface PayoutEvent {
	id: string;
	offer_id: string;
	producer_aff_id: string;
	ngr_cents: number;
	levels: LevelPayout[];
	total_payout_cents: number;
	created_at: number;
}

export interface ProgramResponse {
	program: Program;
}

export interface UplineChainResponse {
	affiliate_id: string;
	self: Upline | null;
	chain: string[];
}

export interface CalculateResponse {
	offer_id: string;
	producer_aff_id: string;
	ngr_cents: number;
	program: Program;
	levels: LevelPayout[];
	total_payout_cents: number;
}

export interface CommitResponse {
	event: PayoutEvent;
	program: Program;
}

export interface EventsResponse {
	offer_id: string | null;
	producer_aff_id: string | null;
	limit: number;
	events: PayoutEvent[];
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const MIN_LEVELS_REQUIRED = 3;
export const MAX_DEPTH = 10;
export const MAX_BPS = 10_000;
export const BPS_DIVISOR = 10_000;

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

/** Format bps as a percent with two decimals (e.g. `5.00%`). */
export function formatBpsPct(bps: number): string {
	if (!Number.isFinite(bps)) return "—";
	return `${(bps / 100).toFixed(2)}%`;
}

/** Format cents as a USD-ish amount (`$1,234.56`). */
export function formatCents(cents: number): string {
	if (!Number.isFinite(cents)) return "$—";
	const abs = Math.abs(cents);
	const whole = Math.floor(abs / 100);
	const frac = (abs % 100).toString().padStart(2, "0");
	const sign = cents < 0 ? "-" : "";
	const groups: string[] = [];
	let w = whole.toString();
	while (w.length > 3) {
		groups.unshift(w.slice(-3));
		w = w.slice(0, -3);
	}
	groups.unshift(w);
	return `${sign}$${groups.join(",")}.${frac}`;
}

/** Format an integer with thousands separator. */
export function formatInteger(n: number): string {
	if (!Number.isFinite(n)) return "0";
	const abs = Math.abs(Math.trunc(n));
	const groups: string[] = [];
	let w = abs.toString();
	while (w.length > 3) {
		groups.unshift(w.slice(-3));
		w = w.slice(0, -3);
	}
	groups.unshift(w);
	return (n < 0 ? "-" : "") + groups.join(",");
}

/** Pretty-print an assigned_at unix-seconds timestamp. */
export function formatUnixDate(unixSec: number): string {
	if (!Number.isFinite(unixSec) || unixSec <= 0) return "—";
	const d = new Date(unixSec * 1000);
	return d.toLocaleDateString("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
	});
}

/**
 * Parse a comma/space/newline-separated bps string into an integer array.
 * Returns [] if any entry is not a valid integer in [0, MAX_BPS].
 */
export function parseBpsList(raw: string): number[] {
	if (typeof raw !== "string") return [];
	const parts = raw
		.split(/[\s,]+/)
		.map((p) => p.trim())
		.filter((p) => p.length > 0);
	const out: number[] = [];
	for (const p of parts) {
		if (!/^-?\d+$/.test(p)) return [];
		const n = Number.parseInt(p, 10);
		if (!Number.isInteger(n) || n < 0 || n > MAX_BPS) return [];
		out.push(n);
	}
	return out;
}

/** Sum the projected total payout for a bps ladder against a given NGR. */
export function projectTotalCents(ngrCents: number, bps: readonly number[]): number {
	if (!Number.isInteger(ngrCents) || ngrCents <= 0) return 0;
	let total = 0;
	for (const b of bps) {
		if (!Number.isInteger(b) || b <= 0) continue;
		total += Math.floor((ngrCents * b) / BPS_DIVISOR);
	}
	return total;
}

/** Default seed for a 3-level program (5% / 3% / 1%). */
export function defaultLevelBps(): number[] {
	return [500, 300, 100];
}

// ---------------------------------------------------------------------------
// API input shapes
// ---------------------------------------------------------------------------

export interface CreateProgramInput {
	offer_id: string;
	max_depth: number;
	level_bps: number[];
	license_ok: boolean;
	active: boolean;
}

export interface UpsertUplineInput {
	affiliate_id: string;
	parent_id: string | null;
}

export interface CalculateInput {
	offer_id: string;
	producer_aff_id: string;
	ngr_cents: number;
}

// ---------------------------------------------------------------------------
// Remote bindings.
// ---------------------------------------------------------------------------

const BASE = "/igaming-multi-level";

export async function createProgram(
	input: CreateProgramInput,
): Promise<ProgramResponse> {
	return apiClient.post<ProgramResponse>(`${BASE}/programs`, input);
}

export async function fetchProgramByOffer(
	offerId: string,
): Promise<ProgramResponse | null> {
	try {
		return await apiClient.get<ProgramResponse>(
			`${BASE}/programs/offer/${encodeURIComponent(offerId)}`,
		);
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return null;
		throw err;
	}
}

export async function setProgramLicense(
	programId: string,
	licenseOk: boolean,
): Promise<ProgramResponse> {
	return apiClient.post<ProgramResponse>(
		`${BASE}/programs/${encodeURIComponent(programId)}/license`,
		{ license_ok: licenseOk },
	);
}

export async function deleteProgram(programId: string): Promise<{ deleted: boolean }> {
	return apiClient.delete<{ deleted: boolean }>(
		`${BASE}/programs/${encodeURIComponent(programId)}`,
	);
}

export async function upsertUpline(
	input: UpsertUplineInput,
): Promise<{ upline: Upline }> {
	return apiClient.post<{ upline: Upline }>(`${BASE}/uplines`, input);
}

export async function fetchUplineChain(
	affiliateId: string,
): Promise<UplineChainResponse> {
	return apiClient.get<UplineChainResponse>(
		`${BASE}/uplines/${encodeURIComponent(affiliateId)}`,
	);
}

export async function calculateCascade(
	input: CalculateInput,
): Promise<CalculateResponse> {
	return apiClient.post<CalculateResponse>(`${BASE}/calculate`, input);
}

export async function commitCascade(input: CalculateInput): Promise<CommitResponse> {
	return apiClient.post<CommitResponse>(`${BASE}/commit`, input);
}

export async function fetchEvents(opts: {
	offer_id?: string;
	producer_aff_id?: string;
	limit?: number;
}): Promise<EventsResponse> {
	const query: Record<string, string | number | undefined> = {};
	if (opts.offer_id) query.offer_id = opts.offer_id;
	if (opts.producer_aff_id) query.producer_aff_id = opts.producer_aff_id;
	if (opts.limit !== undefined) query.limit = opts.limit;
	return apiClient.get<EventsResponse>(`${BASE}/events`, { query });
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const mlmQueryKeys = {
	all: ["igaming-multi-level"] as const,
	program: (offerId: string) => [...mlmQueryKeys.all, "program", offerId] as const,
	upline: (affiliateId: string) => [...mlmQueryKeys.all, "upline", affiliateId] as const,
	events: (scope: { offer_id?: string; producer_aff_id?: string }) =>
		[
			...mlmQueryKeys.all,
			"events",
			scope.offer_id ?? "",
			scope.producer_aff_id ?? "",
		] as const,
};
